import UserInfoItem from '@op/components/Snippets/UserInfoItem';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_U, DBO_V } from '@op/utils/format';
import React, { useMemo } from 'react';
import { Badge, Button } from 'tea-component';
import TableMiniChart from '../component/TableMiniChart';
import BubbleCommon from '@op/components/BubbleCommon';
import { history } from 'umi';
import { DBO_POINT } from '@op/operationLog/buriedPoint';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import TextBubbles from '@home/components/TextBubbles';
import iconUpGreen from '@op/assets/svg/icon-up-green.svg';
import iconDownRed from '@op/assets/svg/icon-down-red.svg';
import followersIcon from '@op/assets/svg/followers.svg';
import ChannelLabel from '@op/components/ChannelLabel';

export function useRealTimeStreamingColumns({
  isCCV,
  filter,
  pageInfo,
  rankTimeList,
  isGST = false,
}: {
  isCCV: boolean;
  filter: any;
  pageInfo: any;
  rankTimeList: {
    newTime: string[];
    oldTime: string[];
  };
  isGST?: boolean;
}) {
  const { formatMessage } = useLanguage();

  function clickUserInfo(cvm: { live_url: string }) {
    DBO_U.open(cvm.live_url);
    DBO_POINT.btn('VisitSourceSite');
  }

  const columns = useMemo(() => {
    return [
      {
        key: 'rank',
        header: formatMessage('Rank'),
        width: 80,
        render: (cvm: { previous_rank: number; current_rank: number }) => {
          const rank = cvm.current_rank || 0;
          const oldRank = cvm.previous_rank || 0;
          const changeNum = oldRank - rank;
          let text: string | any = '-';
          let className = '';
          if (changeNum > 0) {
            text = <img src={iconUpGreen} />;
            className = '--up';
          }

          if (changeNum < 0) {
            text = <img src={iconDownRed} />;
            className = '--down';
          }

          return (
            <div className="dbo-steaming-rank">
              <div className="dbo-steaming-rank-title">{rank}</div>
              {oldRank > 0 && (
                <RingRatioBubble
                  {...rankTimeList}
                  oldValue={oldRank}
                  newValue={rank}
                  showContent
                >
                  <div className={`dbo-steaming-rank-score ${className}`}>
                    {Math.abs(changeNum)} {text}
                  </div>
                </RingRatioBubble>
              )}
            </div>
          );
        },
      },
      {
        key: 'live_anchors',
        header: formatMessage('Streamer'),
        width: 150,
        render: (cvm: {
          live_url: string;
          anchors_image: string;
          live_anchors: string;
          is_featured_anchor: any;
          kol_types: Array<string>;
          ext_json: string;
        }) => {
          const { ext_json } = cvm || {};
          let streamingInfo: { user_cover?: string } = {};
          try {
            const firstParse = JSON.parse(ext_json || '{}');
            streamingInfo = JSON.parse(firstParse || '{}') || firstParse;
          } catch (error) { }

          return (
            <div className="dbo-steaming-user-info">
              <BubbleCommon
                tipsMap={null}
                showIcon={false}
                placement="top"
                placementOffset={[-40, 10]}
                content={formatMessage('Visit original site')}
              >
                <UserInfoItem
                  onClick={() => clickUserInfo(cvm)}
                  iconClick={() => clickUserInfo(cvm)}
                  icon={cvm?.anchors_image || streamingInfo.user_cover}
                  name={cvm.live_anchors}
                >
                  {() => {
                    return (
                      <>
                        {!isGST && (
                          <div className="dbo-steaming-followers">
                            <img src={followersIcon} />
                            <span className="dbo-steaming-followers-num">
                              {DBO_V.reviews(cvm?.anchors_followers || 0)}
                            </span>
                          </div>
                        )}
                        {(cvm?.kol_types || []).includes('featured') && (
                          <Badge theme="success" className="dbo-steaming-tags">
                            {formatMessage('Featured')}
                          </Badge>
                        )}
                        {/* <Badge
                        className="dbo-steaming-status"
                        dot
                        theme="success"
                      /> */}
                      </>
                    );
                  }}
                </UserInfoItem>
              </BubbleCommon>
            </div>
          );
        },
      },
      ...(isGST
        ? [
          {
            key: 'source',
            width: 160,
            header: formatMessage('Source'),
            render: (cvm: { source: string; }) => {
              return (
                <ChannelLabel
                  channel_name={cvm.source as any}
                  showText
                />
              );
            },
          },
        ]
        : []),
      {
        key: 'live_title',
        header: formatMessage('Title'),
        width: 300,
        render: (cvm: { live_title: string; live_url: string }) => {
          return (
            <div className="dbo-steaming-title">
              <TextBubbles text={cvm.live_title} rowNum={2} />
            </div>
          );
        },
      },
      {
        key: 'live_viewer',
        header: formatMessage(isCCV ? 'CCV' : 'PCV'),
        align: 'right',
        width: 150,
        render: (cvm: {
          live_viewer: number;
          live_timeline: {
            timeline_of_start_time: string;
            timeline_of_end_time: string;
            timeline: { live_viewer: number; time: string }[];
          };
        }) => {
          return (
            <div className="dbo-steaming-viewer">
              {cvm?.live_viewer > 0 ? DBO_V.reviews(cvm.live_viewer) : null}
              <TableMiniChart
                name={formatMessage(isCCV ? 'CCV' : 'PCV')}
                data={cvm.live_timeline}
              ></TableMiniChart>
            </div>
          );
        },
      },
      {
        key: 'live_time',
        width: 160,
        header: formatMessage('Stream Start Time'),
        render: (cvm: { live_time: string }) => {
          if (!cvm.live_time) return null;
          return cvm.live_time;
        }
      },
      {
        key: 'live_btn',
        width: 100,
        fixed: 'right',
        header: formatMessage('Action'),
        render: (cvm: {
          live_id: string;
          has_streaming_analysis: boolean;
          has_live_chats_analysis: boolean;
        }) => {
          const disabled = !(
            cvm.has_streaming_analysis || cvm.has_live_chats_analysis
          );
          const contentText = disabled
            ? formatMessage(
              'Drill-down analysis is only supported for featured streamers and popular live streaming rooms.',
            )
            : null;
          return (
            <BubbleCommon
              tipsMap={null}
              showIcon={false}
              placement="top-end"
              content={contentText}
            >
              <Button
                disabled={disabled}
                onClick={() => {
                  history.push(
                    `/opinion/Realtime/RealtimeStreaming/Details/${cvm.live_id
                    }?start_time=${decodeURIComponent(
                      filter.start_time,
                    )}&end_time=${decodeURIComponent(filter.end_time)}`,
                  );
                  DBO_POINT.btn('GotoDetail');
                }}
                type="link"
              >
                {formatMessage('View Details')}
              </Button>
            </BubbleCommon>
          );
        },
      },
    ];
  }, [formatMessage, isCCV, filter, pageInfo, rankTimeList]);

  return { columns };
}
